/** 只读后端 API（与现站 PHP 兼容） */
const json = async (url, opts = {}) => {
  const res = await fetch(url, {
    ...opts,
    headers: {
      Accept: "application/json",
      ...(opts.headers || {}),
    },
  });
  if (!res.ok) throw new Error(`${url} → ${res.status}`);
  return res.json();
};

export function extractIpfsCid(url) {
  const s = String(url || "").trim();
  const m =
    s.match(/\/ipfs\/([a-zA-Z0-9]+)/i) ||
    s.match(/^(?:ipfs:\/\/)?((?:bafy|bafk|Qm)[a-zA-Z0-9]+)/i);
  return m ? m[1] : "";
}

const thumbFailures = new Map();
const THUMB_RETRY_MS = 5 * 60 * 1000;
// Keep the IMG owned by Vue and let the existing initial-letter placeholder show through.
const EMPTY_THUMB = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

function thumbSources(cid) {
  return [
    `/api/ipfs_img.php?cid=${encodeURIComponent(cid)}`,
    `https://flap.mypinata.cloud/ipfs/${cid}`,
    `https://ipfs.io/ipfs/${cid}`,
  ];
}

function thumbFailure(cid) {
  const state = thumbFailures.get(cid);
  if (state && state.until > Date.now()) return state;
  thumbFailures.delete(cid);
  return null;
}

function nextThumbSource(cid) {
  const failed = thumbFailure(cid)?.sources;
  return thumbSources(cid).find((src) => !failed?.has(src)) || EMPTY_THUMB;
}

/** Share failed sources across rows/remounts; retry upstream only after the cooldown. */
export function thumbSrc(url) {
  const raw = String(url || "");
  const cid = extractIpfsCid(raw);
  if (!cid) return raw;
  return nextThumbSource(cid);
}

/**
 * 站内媒体地址（金库封面 / 本地 images / IPFS）
 * 相对路径必须加前导 /，否则在 /deploy/:id 等嵌套路由下会解析到错误 URL。
 */
export function mediaSrc(url) {
  const raw = String(url || "").trim();
  if (!raw) return "";
  if (extractIpfsCid(raw)) return thumbSrc(raw);
  if (/^https?:\/\//i.test(raw) || raw.startsWith("data:") || raw.startsWith("blob:")) {
    return thumbSrc(raw);
  }
  if (raw.startsWith("/")) return raw;
  return `/${raw.replace(/^\.\//, "")}`;
}

/** 缓存命中时 load 可能早于 Vue 绑定 → 用 ref 补一次 is-on */
export function bindThumbEl(el) {
  if (!el || el.tagName !== "IMG") return;
  const mark = () => {
    try {
      if (el.getAttribute("src") === EMPTY_THUMB) {
        el.classList.remove("is-on");
        return;
      }
      el.classList.add("is-on");
    } catch {
      /* ignore */
    }
  };
  if (el.complete && el.naturalWidth > 0) {
    mark();
    return;
  }
  el.addEventListener("load", mark, { once: true });
}

export function markThumbOn(e) {
  const el = e?.target;
  if (el) bindThumbEl(el);
}

/** Bounded fallback: same-origin → Pinata → ipfs.io → existing placeholder. */
export function onThumbError(e, originalUrl) {
  const el = e?.target;
  if (!el || !el.tagName) return;
  const raw = String(originalUrl || el.getAttribute("data-raw") || "");
  const cid = extractIpfsCid(raw);
  const current = el.getAttribute("src") || el.src || "";
  // A blocked data URI (e.g. restrictive CSP) must not cause an error loop either.
  if (current === EMPTY_THUMB) return;
  if (!cid) {
    el.classList.remove("is-on");
    el.setAttribute("data-fb", "3");
    el.src = EMPTY_THUMB;
    return;
  }
  const sources = thumbSources(cid);
  const failedSource = sources.find((src) => current === src || (src.startsWith("/") && current.endsWith(src)));
  const state = thumbFailure(cid) || { sources: new Set(), until: Date.now() + THUMB_RETRY_MS };
  if (failedSource) state.sources.add(failedSource);
  else state.sources.add(current);
  thumbFailures.set(cid, state);
  // Bound this page-session cache even on long-running monitor sessions.
  if (thumbFailures.size > 2000) {
    for (const [key, value] of thumbFailures) if (value.until <= Date.now()) thumbFailures.delete(key);
    if (thumbFailures.size > 2000) thumbFailures.delete(thumbFailures.keys().next().value);
  }
  const next = nextThumbSource(cid);
  el.classList.remove("is-on");
  el.setAttribute("data-fb", String(next === EMPTY_THUMB ? 3 : sources.indexOf(next)));
  if (current !== next) el.src = next;
  queueMicrotask(() => bindThumbEl(el));
}

export const api = {
  deploysList: () => json("/api/deploys.php?view=list"),
  quotes: () => json("/api/quotes.php?t=" + Date.now()),
  feed: (limit = 36) => json(`/api/feed.php?limit=${limit}`),
  factoryShowcase: () => json("/api/factory_showcase.php"),
  gainers: (limit = 200) => json(`/api/gainers.php?limit=${limit}`),
};

export default api;
